import { useCallback, useEffect, useRef, useState } from 'react';
import { APIChatPresence } from '../api';

/**
 * Онлайн-статусы участников одного чата.
 *
 * Источников два, и оба нужны:
 *   • ручка — полная правда при открытии чата и после переподключения;
 *   • событие 'presence' по веб-сокету — мгновенная реакция, пока чат открыт.
 *
 * Только на сокете жить нельзя: он приносит ИЗМЕНЕНИЯ, а не состояние, и
 * тот, кто вошёл до открытия чата, так и остался бы серым. Только на ручке —
 * тоже: опрашивать сервер раз в несколько секунд ради точки расточительно.
 *
 * applyEvent сюда передаёт MessagesPane из обработчика веб-сокета.
 */
export function usePresence(chatId, token, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const reload = useCallback(async () => {
    if (!chatId || !enabled) return;
    setLoading(true);
    const res = await APIChatPresence(chatId, token);
    if (!aliveRef.current) return;
    setLoading(false);
    if (res?.status === 'ok') setData(res);
  }, [chatId, token, enabled]);

  useEffect(() => {
    setData(null);
    reload();
  }, [reload]);

  // Вкладку свернули на час — статусы протухли. Возврат к ней обновляет их
  // разом, без опроса в фоне.
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') reload();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [reload, enabled]);

  /** Применить событие 'presence' из сокета: { actor_key, online, last_seen }. */
  const applyEvent = useCallback((ev) => {
    if (!ev?.actor_key) return;
    setData((prev) => {
      if (!prev) return prev;
      let touched = false;
      const participants = (prev.participants || []).map((p) => {
        if (p.actor_key !== ev.actor_key) return p;
        touched = true;
        return {
          ...p,
          online: !!ev.online,
          last_seen: ev.last_seen || p.last_seen,
          // Точную подпись посчитает сервер при следующей загрузке; до этого
          // показываем понятное «в сети» / «только что» — врать на минуты
          // безопаснее, чем оставлять устаревшее «был вчера» у того, кто
          // прямо сейчас в чате.
          last_seen_text: ev.online ? 'в сети' : 'только что',
        };
      });
      if (!touched) return prev;

      const others = participants.filter((p) => !p.is_me);
      const online = others.filter((p) => p.online);
      return {
        ...prev,
        participants,
        online_count: online.length,
        online_names: online.slice(0, 3).map((p) => p.name),
        peer: others.length === 1 ? others[0] : prev.peer,
      };
    });
  }, []);

  return { presence: data, presenceLoading: loading, reloadPresence: reload, applyPresenceEvent: applyEvent };
}

/** Подпись под названием чата: «в сети» / «был(а) …» / «3 из 8 в сети». */
export function presenceSubtitle(presence) {
  if (!presence) return null;
  if (!presence.is_group) {
    return presence.peer?.last_seen_text || null;
  }
  const n = presence.online_count || 0;
  if (n <= 0) return null;
  const names = presence.online_names || [];
  if (n <= names.length) return `${names.join(', ')} в сети`;
  return `${names.join(', ')} и ещё ${n - names.length} в сети`;
}
